if (!customElements.get('product-card')) {
  customElements.define(
    'product-card',
    class ProductCard extends HTMLElement {
      constructor() {
        super();
        this.product = null; // Initialize product property
        this.swiper = null; // Initialize swipers property
        this.hydrated = false;
        this.hydrationPromise = null;
        this.hydrationObserver = null;
        this.carouselInitPromise = null;
        this.carouselIntentEvents = ['pointerenter', 'focusin', 'touchstart'];
        this.carouselIntentOptions = { once: true, passive: true };
        this.hydrationIntentEvents = ['pointerenter', 'focusin', 'touchstart'];
        this.hydrationIntentOptions = { once: true, passive: true };
        this.handleCarouselIntent = this.handleCarouselIntent.bind(this);
        this.handleHydrationIntent = this.handleHydrationIntent.bind(this);
        this.handleDeferredSwatchClick = this.handleDeferredSwatchClick.bind(this);
        this.handleSwatchClick = this.handleSwatchClick.bind(this);
        this.initCarousel = this.initCarousel.bind(this);
        this.showLifeStyleImageFirst = true; // Initialize showLifeStyleImageFirst property
        this.gridView = 2; // Default to 2 column grid view
        this.cartUpdateUnsubscriber = null;
        this.cartDataLoadedUnsubscriber = null;
      }

      getCommonProductCardData() {
        if (window.luxoProductCardCommonData) return window.luxoProductCardCommonData;

        const script = document.getElementById('ProductCardCommonData');
        if (!script) {
          window.luxoProductCardCommonData = {};
          return window.luxoProductCardCommonData;
        }

        try {
          window.luxoProductCardCommonData = JSON.parse(script.textContent);
        } catch (error) {
          window.luxoProductCardCommonData = {};
        }

        return window.luxoProductCardCommonData;
      }

      connectedCallback() {
        // const mainContent = document.querySelector('#MainContent');
        // const hasMobileLifestyleImage = mainContent?.classList.contains('has-mobile-lifestyle-image');
        // window.addEventListener('vwo:mobile-lifestyle-ready', this.initLifestyleCarousel.bind(this), { once: true });
        const params = new URLSearchParams(window.location.search);

        if (params.get("view") === "one-column") {
          this.gridView = 1;
        }

        if (this.shouldDeferHydration()) {
          this.addDeferredHydrationListeners();
          this.scheduleHydrationNearViewport();
          return;
        }

        this.hydrate();
      }

      shouldDeferHydration() {
        return Boolean(
          this.closest('collection-product-grid') ||
          this.closest('products-carousel[data-lazy-load-assets="true"]')
        );
      }

      addDeferredHydrationListeners() {
        if (this.deferredHydrationListenersAdded) return;
        this.deferredHydrationListenersAdded = true;

        this.hydrationIntentEvents.forEach((eventName) => {
          this.addEventListener(eventName, this.handleHydrationIntent, this.hydrationIntentOptions);
        });
        this.addEventListener('click', this.handleDeferredSwatchClick, true);
      }

      removeDeferredHydrationListeners() {
        if (!this.deferredHydrationListenersAdded) return;
        this.deferredHydrationListenersAdded = false;

        this.hydrationIntentEvents.forEach((eventName) => {
          this.removeEventListener(eventName, this.handleHydrationIntent, this.hydrationIntentOptions);
        });
        this.removeEventListener('click', this.handleDeferredSwatchClick, true);
      }

      scheduleHydrationNearViewport() {
        if (this.hydrationObserver || this.hydrated) return;

        if (!('IntersectionObserver' in window)) {
          this.runWhenIdle(() => this.hydrate(), 3000);
          return;
        }

        this.hydrationObserver = new IntersectionObserver((entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.hydrationObserver = null;
          this.runWhenIdle(() => this.hydrate(), 1200);
        }, { rootMargin: '250px 0px' });

        this.hydrationObserver.observe(this);
      }

      runWhenIdle(callback, fallbackDelay) {
        if (window.luxoRunWhenIdle) {
          window.luxoRunWhenIdle(callback, fallbackDelay);
        } else if ('requestIdleCallback' in window) {
          requestIdleCallback(callback, { timeout: fallbackDelay });
        } else {
          setTimeout(callback, fallbackDelay);
        }
      }

      handleHydrationIntent() {
        this.hydrate().then(() => {
          if (this.querySelector('.swiper-container')) this.loadCarousel();
        });
      }

      handleDeferredSwatchClick(event) {
        if (this.hydrated) return;

        const swatch = event.target.closest('[data-swatch]');
        if (!swatch || !this.contains(swatch)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        this.hydrate().then(() => {
          swatch.dispatchEvent(new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          }));
        });
      }

      hydrate() {
        if (this.hydrated) return Promise.resolve(this);
        if (this.hydrationPromise) return this.hydrationPromise;

        this.hydrationPromise = Promise.resolve().then(() => {
          if (this.hydrationObserver) {
            this.hydrationObserver.disconnect();
            this.hydrationObserver = null;
          }
          this.removeDeferredHydrationListeners();

        // Fetch product data from attributes or embedded JSON
        const script = this.querySelector('script[type="application/json"]');
        if (script) {
          const cardData = JSON.parse(script.textContent);
          const { product, metafields, variants, translations, settings } = cardData;
          const commonData = this.getCommonProductCardData();
          this.product = product;
          if (!this.product) return;
          this.product.metafields = metafields.custom;
          this.metafieldColourOptions = metafields.colour_options;
          this.product.metafields.bundled_products = metafields.bundled_products;
          this.variants = variants;
          this.translations = { ...(commonData.translations || {}), ...(translations || {}) };
          this.settings = { ...(commonData.settings || {}), ...(settings || {}) };
          this.productCardHeading = this.querySelector('.card__heading a');
          this.sectionId = this.dataset.sectionId;

          // Push product SKUs to promise dates queue
          if (this.metafieldColourOptions && this.metafieldColourOptions.length > 0) {
            this.metafieldColourOptions.forEach((colourOption) => {
              const optionProduct = colourOption?.product ?? {};

              const mainVariant =
                this.product?.selected_or_first_available_variant ??
                this.product?.first_available_variant ??
                this.product?.variants?.[0];

              const mainSku = mainVariant?.sku ?? null;

              const optionVariant =
                optionProduct?.selected_or_first_available_variant ??
                optionProduct?.first_available_variant ??
                optionProduct?.variants?.[0];

              const colourOptionSku = optionVariant?.sku ?? null;

              optionProduct.metafields = optionProduct.metafields || {};

              optionProduct.metafields.bundled_products = [];

              if (mainSku && colourOptionSku && mainSku === colourOptionSku) {
                optionProduct.metafields.bundled_products =
                  (this.product?.metafields && this.product.metafields.bundled_products) || [];
              }

              // Push product to promise dates queue
              this.updatePromiseDate(optionProduct);
            });
          } else {
            this.updatePromiseDate(this.product);
          }

          // Collection cards start as plain images; hydrate the carousel only when a shopper shows intent.
          if (this.querySelector('.swiper-container')) {
            if (this.closest('collection-product-grid')) {
              this.addCarouselIntentListeners();
            } else {
              window.luxoRunNearElement(this, () => this.loadCarousel());
            }
          }

          // Initialize the product card based on the user's location
          // Maybe in the future will use this.updateProductCard method, but for now, would prefer just to run logic
          // when it is required on the first load and leave the initial render html as it is from liquid.
          // Init Product Card based on location will contain things such as inventory status, and promise dates
          this.initProductCardBasedOnLocation(this.product);
        }
        this.swatches = this.querySelectorAll('[data-swatch]');
        this.productHeadings = this.querySelectorAll('.card__heading a');
        this.prices = this.querySelectorAll('.price');
        this.salePrices = this.querySelectorAll('.price__sale');
        this.regularPrices = this.querySelectorAll('.price__regular');
        this.activeCouponPromo = JSON.parse(this.querySelector('.card__badge [data-countdown-sale-badge]')?.dataset.couponPromo || null);
        this.addEventListeners();
        this.subscribeToCartEvents();
        this.updateCountdown()
        this.hydrated = true;
        return this;
        });

        return this.hydrationPromise;
      }

      initLifestyleCarousel() {
        if (!this.hydrated) {
          this.hydrate().then(() => {
            if (this.hydrated) this.initLifestyleCarousel();
          });
          return;
        }

        this.showLifeStyleImageFirst = true;
        if (this.showLifeStyleImageFirst && (this.querySelectorAll('.swiper-slide').length > 1)) {
          // Swap the first and second slides
          const firstSlide = this.querySelectorAll('.swiper-slide')[0].innerHTML;
          const secondSlide = this.querySelectorAll('.swiper-slide')[1].innerHTML;
          this.querySelectorAll('.swiper-slide')[0].innerHTML = secondSlide;
          this.querySelectorAll('.swiper-slide')[1].innerHTML = firstSlide;
        }

        if (this.querySelector('.swiper-container')) {
          this.loadCarousel();
        }
      }

      disconnectedCallback() {
        if (this.hydrationObserver) {
          this.hydrationObserver.disconnect();
          this.hydrationObserver = null;
        }
        this.removeDeferredHydrationListeners();
        this.removeCarouselIntentListeners();
        this.swatches?.forEach(swatch => {
          swatch.removeEventListener('click', this.handleSwatchClick);
        });
        this.unsubscribeFromCartEvents();

        // Destroy the Swiper instance
        if (this.swiper) {
          this.swiper.destroy();
          this.swiper = null;
        }
      }

      addCarouselIntentListeners() {
        if (this.carouselIntentListenersAdded || this.swiper) return;
        this.carouselIntentListenersAdded = true;
        this.carouselIntentEvents.forEach((eventName) => {
          this.addEventListener(eventName, this.handleCarouselIntent, this.carouselIntentOptions);
        });
      }

      removeCarouselIntentListeners() {
        if (!this.carouselIntentListenersAdded) return;
        this.carouselIntentListenersAdded = false;
        this.carouselIntentEvents.forEach((eventName) => {
          this.removeEventListener(eventName, this.handleCarouselIntent, this.carouselIntentOptions);
        });
      }

      handleCarouselIntent() {
        this.loadCarousel();
      }

      subscribeToCartEvents() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        if (!this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            this.handleCartUpdate(event);
          });
        }

        if (!this.cartDataLoadedUnsubscriber) {
          this.cartDataLoadedUnsubscriber = subscribe(PUB_SUB_EVENTS.cartDataLoaded, (cartData) => {
            this.updateProductQuantitiesFromCart(cartData);
          });
        }
      }

      unsubscribeFromCartEvents() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
          this.cartUpdateUnsubscriber = null;
        }

        if (this.cartDataLoadedUnsubscriber) {
          this.cartDataLoadedUnsubscriber();
          this.cartDataLoadedUnsubscriber = null;
        }
      }

      handleCartUpdate(event = {}) {
        const cartData = event.cartData || {};

        if (Array.isArray(cartData.items)) {
          this.updateProductQuantitiesFromCart(cartData);
          return;
        }

        const variantId = event.productVariantId || event.variantId || cartData.variant_id || cartData.id;
        if (!variantId || cartData.quantity === undefined) return;

        this.updateProductQuantityByVariantId(variantId, cartData.quantity);
      }

      loadCarousel() {
        if (this.swiper) return Promise.resolve(this.swiper);
        if (this.carouselInitPromise) return this.carouselInitPromise;

        this.removeCarouselIntentListeners();
        this.carouselInitPromise = window.luxoLoadSwiper()
          .then(() => {
            const swiper = this.initCarousel();
            if (!swiper) this.carouselInitPromise = null;
            return swiper;
          })
          .catch((error) => {
            console.warn('Unable to initialize product card carousel:', error);
            this.carouselInitPromise = null;
            return null;
          });

        return this.carouselInitPromise;
      }

      addEventListeners() {
        this.swatches.forEach(swatch => {
          swatch.addEventListener('click', this.handleSwatchClick);
        });
        // Disabling old grid view toggle event listener
        // window.addEventListener('toggleGridView', (event) => {
        //   this.gridView = event.detail.column;
        //   this.updateProductImagesCarousel(this.product, false);
        // });
      }

      handleSwatchClick(event) {
        const currentTarget = event.currentTarget;
        const productId = parseInt(currentTarget.dataset.productId);
        const variantId = parseInt(currentTarget.dataset.variantId);
        let selectedProductItem;
        let selectedProduct;
        let isVariant = false;
        if (productId) {
          selectedProductItem = this.metafieldColourOptions.find(productItem => productItem.product.id === productId);
          selectedProduct = selectedProductItem.product;
        } else if (variantId) {
          selectedProduct = this.variants.find(variant => variant.id === variantId);
          isVariant = true;
        }
        if (!selectedProduct)
          return;

        const bundledProducts = selectedProduct?.metafields?.bundled_products || [];
        if (selectedProductItem && selectedProductItem.metafields) {
          selectedProduct.metafields = selectedProductItem.metafields;
          selectedProduct.metafields.bundled_products = bundledProducts;
        }

        if (selectedProductItem && selectedProductItem.product_qty_in_cart)
          selectedProduct.product_qty_in_cart = selectedProductItem.product_qty_in_cart;

        if (selectedProductItem && selectedProductItem.product_badge)
          selectedProduct.product_badge = selectedProductItem.product_badge;

        if (selectedProduct) {
          this.updateProductCard(selectedProduct, isVariant);
        }
        // Remove the 'selected' class from all swatches
        this.swatches.forEach(s => s.classList.remove('selected'));

        // Add the 'selected' class to the clicked swatch
        const clickedSwatch = event.currentTarget;
        clickedSwatch.classList.add('selected');
      }

      initProductCardBasedOnLocation(product) {
        // Find the corresponding badge
        // this.updateProductBadges(product);
      }

      updateProductCard(product, isVariant) {
        this.updateProductCardId(product);
        if (!isVariant) {
          this.updateProductHeading(product);
        }
        this.updateProductPricing(product);
        this.updateProductImagesCarousel(product, isVariant);
        this.updateProductBadges(product, isVariant);
        this.updateProductQtyInCart(product);
        this.updatePromiseDate(product);
        this.updateProductFeatureBadge(product);
      }

      updateProductFeatureBadge(product) {
        const featureBadges = this.querySelectorAll('.product-card-feature-badge');
        featureBadges.forEach(el => el.innerHTML = '');
        if (product.product_badge?.image?.src) {
          const productBadge = product.product_badge;
          const featureBadgePosition1 = this.querySelector('.product-card-feature-badge[data-position="1"]');
          const featureBadgePosition2 = this.querySelector('.product-card-feature-badge[data-position="2"]');
          const productImageSrc = productBadge.image?.src || productBadge.image?.value || '';
          const productImageAlt = productBadge.image?.alt || productBadge.title || '';

          if (featureBadgePosition1) {
            featureBadgePosition1.innerHTML = `<img src="${productImageSrc}" alt="${productImageAlt}" loading="lazy" width="100" height="100">`;
          }
          if (featureBadgePosition2) {
            featureBadgePosition2.innerHTML = `<img src="${productImageSrc}" alt="${productImageAlt}" loading="lazy" width="100" height="100">`;
          }
        }
      }

      updateProductCardId(product) {
        this.product = product;
        this.dataset.productId = product.id;
      }

      updatePromiseDate(product) {
        if (this.product.id !== product.id)
          return
        if (this.product.available) {
          if (promiseDateProductCards.isProductSaved(product)) {
            const savedProduct = promiseDateProductCards.getSavedProduct(product);
            promiseDateProductCards.attachPromiseDateToProductCard(savedProduct);
          } else {
            if (!promiseDateProductCards.isProductInQueue(product) && !promiseDateProductCards.isProductSaved(product)) {
              promiseDateProductCards.putProductInQueue(product);
            }
          }
        } else {
          this.querySelector('.card__promise-date').innerHTML = '<span class="sold-out">Sold Out</span>';
        }
      }

      updateProductBadges(product, isVariant) {
        // Find the corresponding badge
        const cardBadges = this.querySelector('.card__badge');
        cardBadges.innerHTML = '';

        if (cardBadges) {
          if (product.available === false && this.settings.show_sold_out_badge) {
            this.updateSoldOutBadge(cardBadges, product);
          } else {
            if (this.settings.show_new_badge)
              this.updateNewBadge(cardBadges, product);
            if (this.isProductOnSale(product) || this.activeCouponPromo) {
              const countdown = this.getCountdown(product);
              if (countdown.countdownActive && this.settings.show_countdown_sale_badge) {
                this.updateCountdownSaleBadge(cardBadges, product, countdown);
              } else {
                this.updateSalesBadge(cardBadges, product);
              }
            }
            if (this.settings.show_preorder_badge)
              this.updatePreorderBadge(cardBadges, product);
            // Can only update low in stock badge if know warehouse location of product relative to delivery location
            if (this.settings.show_low_stock_badge)
              this.updateLowStockBadge(cardBadges, product);
            // if (!isVariant) {
            //   this.updateClearanceBadge(cardBadges, product);
            //   this.updateLuxoLiveBadge(cardBadges, product);
            // }
          }
        }
      }

      updateProductQtyInCart(product) {
        if (!product) return;

        const selectedProductQtyInCart = product.product_qty_in_cart ? product.product_qty_in_cart : 0;
        const cardAddedToCart = this.querySelector('.card__added-to-cart');
        if (!cardAddedToCart) return;

        cardAddedToCart.innerHTML = '';

        if (Number(selectedProductQtyInCart) > 0) {
          const addedToCartHTML = `<div class="card__added-to-cart-icon">
            <div class="in-cart-icon">
              <div class="in-cart-icon-qty">${selectedProductQtyInCart}</div>
              <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_39_12)"><path d="M20.9813 5.45777C20.8463 5.92732 20.7053 6.39564 20.5783 6.86702C19.9964 9.0273 19.42 11.1894 18.8393 13.3497C18.5743 14.3364 17.8532 14.8896 16.8365 14.8902C13.7549 14.8908 10.6732 14.8908 7.59152 14.889C7.42239 14.889 7.25264 14.8695 7.05298 14.8573C7.1635 15.2657 7.27218 15.6675 7.38514 16.0852C7.48162 16.0852 7.56954 16.0852 7.65686 16.0852C10.7318 16.0852 13.8068 16.0833 16.8817 16.0864C18.1817 16.0876 19.2179 17.1562 19.1727 18.4372C19.1397 19.3702 18.5279 20.1963 17.6474 20.4967C16.77 20.7965 15.8065 20.5303 15.1934 19.8177C14.5975 19.1253 14.4919 18.1038 14.9315 17.2874C14.9663 17.2227 15.0017 17.1592 15.0475 17.0756H9.3482C9.38179 17.1397 9.41232 17.1971 9.44223 17.2551C9.88064 18.1136 9.84584 18.9531 9.26333 19.7213C8.6912 20.4753 7.90048 20.7635 6.97604 20.5517C6.02596 20.3343 5.45017 19.7084 5.25417 18.7565C5.07343 17.8779 5.47459 16.9693 6.24638 16.4729C6.38194 16.3856 6.40025 16.3117 6.35934 16.1621C5.39033 12.6365 4.42376 9.11034 3.46512 5.58233C3.10243 4.24635 2.7605 2.90487 2.40941 1.56584C2.39231 1.5005 2.37277 1.43578 2.35079 1.35885H0V0.380063H0.189895C0.99649 0.380063 1.80248 0.379452 2.60907 0.380063C2.99497 0.380063 3.13052 0.486306 3.2331 0.862432C3.57137 2.10621 3.90903 3.35061 4.2473 4.595C4.265 4.66033 4.28393 4.72505 4.30774 4.80871H4.57152C9.81958 4.80871 15.067 4.80993 20.3151 4.80504C20.6173 4.80504 20.8481 4.88564 20.9806 5.17018V5.45716L20.9813 5.45777ZM4.54832 5.80458C4.56481 5.87725 4.5758 5.93708 4.59167 5.99509C5.24684 8.3758 5.90201 10.7559 6.55718 13.1366C6.71349 13.7051 7.04626 13.956 7.64587 13.956C10.6933 13.956 13.7402 13.956 16.7877 13.956C17.4233 13.956 17.739 13.71 17.9032 13.0933C18.4045 11.2083 18.9077 9.32344 19.409 7.43793C19.5525 6.89877 19.6941 6.35962 19.8407 5.80458H4.54832ZM16.9129 19.6535C17.6376 19.6565 18.2214 19.0765 18.2183 18.3566C18.2153 17.6465 17.6346 17.0652 16.9257 17.0627C16.207 17.0603 15.6166 17.6483 15.619 18.3633C15.6221 19.0759 16.1979 19.6498 16.9123 19.6529L16.9129 19.6535ZM7.4633 19.6535C8.18868 19.6553 8.76386 19.0807 8.76264 18.356C8.76142 17.644 8.18502 17.0646 7.47551 17.0633C6.76356 17.0615 6.18166 17.6355 6.17616 18.345C6.17067 19.074 6.73974 19.6523 7.46269 19.6541L7.4633 19.6535Z" fill="black"></path></g><defs><clipPath id="clip0_39_12"><rect width="20.9813" height="20.24" fill="white" transform="translate(0 0.380005)"></rect></clipPath></defs>
              </svg>
            </div>
          </div>`;
          cardAddedToCart.innerHTML = addedToCartHTML;
        }
      }

      updateProductQuantitiesFromCart(cartData = {}) {
        if (!Array.isArray(cartData.items)) return;

        const quantityByVariantId = this.buildCartVariantQuantityMap(cartData.items);
        this.setProductQtyFromCartMap(this.product, quantityByVariantId);

        if (Array.isArray(this.metafieldColourOptions)) {
          this.metafieldColourOptions.forEach((colourOption) => {
            if (!colourOption.product) return;

            colourOption.product_qty_in_cart = this.getProductQtyFromCartMap(colourOption.product, quantityByVariantId);

            if (this.getProductVariantId(this.product) === this.getProductVariantId(colourOption.product)) {
              this.product.product_qty_in_cart = colourOption.product_qty_in_cart;
            }
          });
        }

        if (Array.isArray(this.variants)) {
          this.variants.forEach((variant) => {
            variant.product_qty_in_cart = this.getProductQtyFromCartMap(variant, quantityByVariantId);

            if (this.getProductVariantId(this.product) === this.getProductVariantId(variant)) {
              this.product.product_qty_in_cart = variant.product_qty_in_cart;
            }
          });
        }

        this.updateProductQtyInCart(this.product);
      }

      updateProductQuantityByVariantId(variantId, quantity) {
        const selectedVariantId = Number(variantId);
        if (!selectedVariantId) return;

        const quantityInCart = Number(quantity) || 0;
        let shouldRender = false;

        if (this.getProductVariantId(this.product) === selectedVariantId) {
          this.product.product_qty_in_cart = quantityInCart;
          shouldRender = true;
        }

        if (Array.isArray(this.metafieldColourOptions)) {
          this.metafieldColourOptions.forEach((colourOption) => {
            if (this.getProductVariantId(colourOption.product) !== selectedVariantId) return;

            colourOption.product_qty_in_cart = quantityInCart;
            if (this.getProductVariantId(this.product) === selectedVariantId) {
              this.product.product_qty_in_cart = quantityInCart;
              shouldRender = true;
            }
          });
        }

        if (Array.isArray(this.variants)) {
          this.variants.forEach((variant) => {
            if (this.getProductVariantId(variant) !== selectedVariantId) return;

            variant.product_qty_in_cart = quantityInCart;
            if (this.getProductVariantId(this.product) === selectedVariantId) {
              this.product.product_qty_in_cart = quantityInCart;
              shouldRender = true;
            }
          });
        }

        if (shouldRender) {
          this.updateProductQtyInCart(this.product);
        }
      }

      buildCartVariantQuantityMap(items) {
        return items.reduce((quantityByVariantId, item) => {
          const variantId = Number(item.variant_id || item.id);
          const quantity = Number(item.quantity) || 0;

          if (variantId) {
            quantityByVariantId.set(variantId, (quantityByVariantId.get(variantId) || 0) + quantity);
          }

          return quantityByVariantId;
        }, new Map());
      }

      setProductQtyFromCartMap(product, quantityByVariantId) {
        if (!product) return;

        product.product_qty_in_cart = this.getProductQtyFromCartMap(product, quantityByVariantId);
      }

      getProductQtyFromCartMap(product, quantityByVariantId) {
        return quantityByVariantId.get(this.getProductVariantId(product)) || 0;
      }

      getProductVariantId(product) {
        return Number(
          product?.selected_or_first_available_variant?.id ||
          product?.first_available_variant?.id ||
          product?.variant_id ||
          product?.id
        );
      }

      updateClearanceBadge(cardBadges, product) {
        const clearanceBadgeText = this.translations.clearance;
        const clearanceBadgeColor = this.settings.clearance_badge_color_scheme;

        const searchTerm = 'clearance';
        const isClearanceItem = (product.tags || []).some(tag => tag.toLowerCase() === searchTerm.toLowerCase());

        // Check product tags for clearance
        if (isClearanceItem) {
          // Add the badge
          if (!cardBadges.querySelector('[data-clearance-badge]')) {
            // Create the badge
            const clearanceBadge = document.createElement('span');
            // clearanceBadge.id = `NoMediaStandardBadge-${this.sectionId}-${product.id}`;
            clearanceBadge.classList.add('badge', `badge--top-left`, `color-${clearanceBadgeColor}`);
            clearanceBadge.dataset.clearanceBadge = '';
            clearanceBadge.textContent = clearanceBadgeText;

            cardBadges.appendChild(clearanceBadge);
          }
        } else {
          // Remove the badge
          if (cardBadges.querySelector('[data-clearance-badge]')) {
            cardBadges.querySelector('[data-clearance-badge]').remove();
          }
        }
      }

      updateLuxoLiveBadge(cardBadges, product) {
        const luxoLiveHTML = this.translations.luxo_live_html;

        const searchTerm = 'luxo live';
        const isLuxoLiveItem = (product.tags || []).some(tag => tag.toLowerCase() === searchTerm.toLowerCase());

        // Check product tags for clearance
        if (isLuxoLiveItem) {
          // Add the badge
          if (!cardBadges.querySelector('[data-luxo-live-badge]')) {
            // Create the badge
            const luxoLiveBadge = document.createElement('span');
            // clearanceBadge.id = `NoMediaStandardBadge-${this.sectionId}-${product.id}`;
            luxoLiveBadge.classList.add('badge', `badge--top-left`, `color-luxo-live`, `luxo-live-badge`);
            luxoLiveBadge.dataset.luxoLiveBadge = '';

            luxoLiveBadge.innerHTML = luxoLiveHTML;

            cardBadges.appendChild(luxoLiveBadge);
          }
        } else {
          // Remove the badge
          if (cardBadges.querySelector('[data-luxo-live-badge]')) {
            cardBadges.querySelector('[data-luxo-live-badge]').remove();
          }
        }
      }

      updateLowStockBadge(cardBadges, product) {
        const lowStockBadgeText = this.translations.low_stock;
        const lowStockBadgeColor = this.settings.low_stock_badge_color_scheme;
        const lowStockThreshold = parseInt(this.settings.low_stock_threshold);

        let inventoryQuantity = 0;
        const productWarehouseLocation = 'WP';

        // Get products inventory quantity based on the warehouse location
        // Warehouse location will be determined by the users selected location and retrieved from the shipping calculator
        if (productWarehouseLocation == 'BNE') {
          inventoryQuantity = product.metafields.inventory_brisbane_warehouse;
        } else if (productWarehouseLocation == 'PER') {
          inventoryQuantity = product.metafields.inventory_perth_warehouse;
        } else {
          // Use Wetherill Park as the default warehouse location
          inventoryQuantity = product.metafields.inventory_sydney_warehouse;
        }

        if (inventoryQuantity <= lowStockThreshold) {
          // Add the badge
          if (!cardBadges.querySelector('[data-low-stock-badge]')) {
            // Create the badge
            const lowStockBadge = document.createElement('span');
            // lowStockBadge.id = `NoMediaStandardBadge-${this.sectionId}-${product.id}`;
            lowStockBadge.classList.add('badge', `badge--top-right`, `color-${lowStockBadgeColor}`);
            lowStockBadge.dataset.lowStockBadge = '';
            lowStockBadge.textContent = lowStockBadgeText;

            cardBadges.appendChild(lowStockBadge);
          }
        } else {
          // Remove the badge
          if (cardBadges.querySelector('[data-low-stock-badge]')) {
            cardBadges.querySelector('[data-low-stock-badge]').remove();
          }
        }
      }

      updateSoldOutBadge(cardBadges, product) {
        const soldOutBadgeText = this.translations.sold_out;
        const soldOutBadgeColor = this.settings.sold_out_badge_color_scheme;

        if (product.available === false) {
          // Add the badge
          if (!cardBadges.querySelector('[data-sold-out-badge]')) {
            // Create the badge
            const soldOutBadge = document.createElement('span');
            // soldOutBadge.id = `NoMediaStandardBadge-${this.sectionId}-${product.id}`;
            soldOutBadge.classList.add('badge', `badge--top-left`, `color-${soldOutBadgeColor}`);
            soldOutBadge.dataset.soldOutBadge = '';
            soldOutBadge.textContent = soldOutBadgeText;

            cardBadges.appendChild(soldOutBadge);
          }
        } else {
          // Remove the badge
          if (cardBadges.querySelector('[data-sold-out-badge]')) {
            cardBadges.querySelector('[data-sold-out-badge]').remove();
          }
        }
      }

      updatePreorderBadge(cardBadges, product) {
        const preorderBadgeText = this.translations.preorder;
        const preorderBadgeColor = this.settings.preorder_badge_color_scheme;

        const productWarehouseLocation = 'WP';
        let productIsPreorder = false;

        // Get products preorder status based on the warehouse location
        // Warehouse location will be determined by the users selected location and retrieved from the shipping calculator
        if (!product.metafields) return;
        if (productWarehouseLocation == 'BNE') {
          productIsPreorder = product.metafields.preorder_brisbane_warehouse;
        } else if (productWarehouseLocation == 'PER') {
          productIsPreorder = product.metafields.preorder_perth_warehouse;
        } else {
          // Use Wetherill Park as the default warehouse location
          productIsPreorder = product.metafields.preorder_sydney_warehouse;
        }

        if (productIsPreorder) {
          // Add the badge
          if (!cardBadges.querySelector('[data-preorder-badge]')) {
            // Create the badge
            const preorderBadge = document.createElement('span');
            // preorderBadge.id = `NoMediaStandardBadge-${this.sectionId}-${product.id}`;
            preorderBadge.classList.add('badge', `badge--top-right`, `color-${preorderBadgeColor}`);
            preorderBadge.dataset.preorderBadge = '';
            preorderBadge.textContent = preorderBadgeText;

            cardBadges.appendChild(preorderBadge);
          }
        } else {
          // Remove the badge
          if (cardBadges.querySelector('[data-preorder-badge]')) {
            cardBadges.querySelector('[data-preorder-badge]').remove();
          }
        }
      }

      isProductNew(product) {
        if (product)
          return (product.tags || []).some(tag => tag.toLowerCase() === 'new furniture');
        return false
      }

      updateNewBadge(cardBadges, product) {
        const newBadgeText = this.translations.new;
        // Check if Product is New
        if (this.isProductNew(product)) {
          // Add the badge
          if (!cardBadges.querySelector('[data-new-badge]')) {
            // Create the badge
            const newBadge = document.createElement('span');
            // newBadge.id = `NoMediaStandardBadge-${this.sectionId}-${product.id}`;
            newBadge.classList.add('badge', `badge--top-left`, `color-${this.settings.new_badge_color_scheme}`);
            newBadge.dataset.newBadge = '';
            newBadge.textContent = newBadgeText;

            cardBadges.appendChild(newBadge);
          }
        } else {
          // Remove the badge
          if (cardBadges.querySelector('[data-new-badge]')) {
            cardBadges.querySelector('[data-new-badge]').remove();
          }
        }
      }

      getCountdown(product) {
        let countdownActive = false;
        let timeLeft = {
          daysLeft: 0,
          hoursLeft: 0,
          minutesLeft: 0,
          message: ''
        }

        try {
          const todayTimestamp = Math.floor(SYDNEY_DATE_NOW / 1000); // Current time in Unix timestamp (seconds)
          if ((!product.metafields.sale_start_date_time || !product.metafields.sale_end_date_time) && !this.activeCouponPromo) return { countdownActive, timeLeft };
          const endDate = product.metafields.sale_end_date_time || this.activeCouponPromo?.end_date;
          const startDate = product.metafields.sale_start_date_time || this.activeCouponPromo?.start_date;
          let productSaleStartTimestamp = Math.floor(new Date(startDate)?.getTime() / 1000) || 0; // Product creation time in Unix timestamp (seconds)
          let productSaleEndTimestamp = Math.floor(new Date(endDate)?.getTime() / 1000) || 0; // Product creation time in Unix timestamp (seconds)
          productSaleEndTimestamp = getDayDifference(SYDNEY_DATE_NOW, new Date(endDate)) > 7 ?
            getDateBlocks(SYDNEY_DATE_NOW, endDate, 7).find((block) => block.end > SYDNEY_DATE_NOW && block.start <= SYDNEY_DATE_NOW)?.end.getTime() / 1000
            : productSaleEndTimestamp

          if (!productSaleStartTimestamp || !productSaleEndTimestamp) return { countdownActive, timeLeft };

          if (todayTimestamp >= productSaleStartTimestamp && todayTimestamp <= productSaleEndTimestamp) {
            countdownActive = true;
            // Get days, hours, minutes left in sale
            const daysLeft = Math.floor((productSaleEndTimestamp - todayTimestamp) / 86400);
            const hoursLeft = Math.floor((productSaleEndTimestamp - todayTimestamp) / 3600);
            const minutesLeft = Math.floor((productSaleEndTimestamp - todayTimestamp) / 60) % 60;
            let remainingTimeLeftMessage = "";

            // Get Time Left
            if (daysLeft > 0) {
              remainingTimeLeftMessage = `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
            } else if (hoursLeft > 0) {
              remainingTimeLeftMessage = `${hoursLeft} ${hoursLeft === 1 ? 'hour' : 'hours'} left`;
            } else if (minutesLeft > 0) {
              remainingTimeLeftMessage = `${minutesLeft} ${minutesLeft === 1 ? 'minute' : 'minutes'} left`;
            }
            timeLeft = {
              daysLeft,
              hoursLeft,
              minutesLeft,
              message: remainingTimeLeftMessage
            };
          }

          return {
            countdownActive,
            timeLeft
          }
        } catch (error) {
          console.warn('Error calculating countdown:', error, product);
        }
      }

      isPreorderSale(product = this.product) {
        return product.metafields && product.metafields.preorder_discount && product.metafields.preorder_discount;
      }

      updateCountdown() {
        if (this.isPreorderSale(this.product) && !this.activeCouponPromo)
          return
        const countdown = this.getCountdown(this.product);
        if (this.querySelector('.sales-badge-countdown .countdown-text') && countdown.timeLeft.message !== this.querySelector('.sales-badge-countdown .countdown-text')?.textContent)
          this.querySelector('.sales-badge-countdown .countdown-text').textContent = countdown.timeLeft.message;
      }

      updateCountdownSaleBadge(cardBadges, product, countdown) {
        const onSaleBadgeText = this.translations.on_sale;
        const countdownSaleBadgeColor = this.settings.countdown_sale_badge_color_scheme;
        const couponDiscount = this.activeCouponPromo ? this.activeCouponPromo.is_percent ? (product.price * +this.activeCouponPromo.amount_off) / 100 : +this.activeCouponPromo.amount_off * 100 : 0;
        const compare_at_price = !!product.compare_at_price ? product.compare_at_price : product.price;
        const salePrice = product.price - couponDiscount;
        // Is product in sale period
        if (this.isPreorderSale(product) || countdown.countdownActive) {
          if (!cardBadges.querySelector('[data-countdown-sale-badge]')) {
            // Create the badge
            const countdownSaleBadge = document.createElement('span');
            // countdownSaleBadge.id = `NoMediaStandardBadge-${this.sectionId}-${product.id}`;
            countdownSaleBadge.classList.add('badge', `badge--bottom-right`, `color-${countdownSaleBadgeColor}`, `badge--promo-${this.settings.promotion_badge_design_scheme}`);
            countdownSaleBadge.dataset.countdownSaleBadge = '';
            let remainingTimeLeftMessage = countdown.timeLeft.message;
            const badgeStyles = { bg_color: this.activeCouponPromo?.background_color ?? 'var(--sale-color)', fg_color: this.activeCouponPromo?.foreground_color ?? '#ffffff' }
            if (this.isPreorderSale(product) && !this.activeCouponPromo) {
              countdownSaleBadge.classList.add('early-bird-tag');
              remainingTimeLeftMessage = 'Early Bird Deal';
              badgeStyles.bg_color = 'var(--preorder-color)';
              badgeStyles.fg_color = '#ffffff';
            }
            let countdownSalesBadgeHtml = `<div class="sales-badge-countdown">
                <div class="badge-text" style='--bg-color: ${badgeStyles.bg_color}; --fg-color: ${badgeStyles.fg_color};'>`;
            if (this.settings.promotion_badge_off_value == 'dollar_off') {
              const dollarOffValue = compare_at_price - salePrice;
              const formattedDollarOffValue = (dollarOffValue / 100).toFixed(2).replace('.00', '');
              countdownSalesBadgeHtml += `<span class="badge-text__off">$${formattedDollarOffValue} Off</span>`;
            } else if (this.settings.promotion_badge_off_value == 'percent_off') {
              const percentageOffValue = Math.floor((compare_at_price - salePrice) / compare_at_price * 100);
              countdownSalesBadgeHtml += `<span class="badge-text__off">${percentageOffValue}% Off</span>`;
            } else {
              countdownSalesBadgeHtml += `<span class="badge-text__off">${onSaleBadgeText}</span>`;
            }
            if (this.settings.promotion_badge_design_scheme == 'christmas') {
              countdownSalesBadgeHtml += `<span class="badge-message">Xmas Sale</span>`;
            } else if (this.settings.promotion_badge_design_scheme == 'black-friday') {
              countdownSalesBadgeHtml += `<span class="badge-message">Black Friday</span>`;
            } else if (this.settings.promotion_badge_design_scheme == 'cyber-week') {
              countdownSalesBadgeHtml += `<span class="badge-message">Cyber Week</span>`;
            }
            countdownSalesBadgeHtml += `</div>
                <div class="countdown-text">${remainingTimeLeftMessage}</div>
              </div>`;

            countdownSaleBadge.innerHTML = countdownSalesBadgeHtml;

            cardBadges.appendChild(countdownSaleBadge);
          }
        } else {
          // Remove the badge
          if (cardBadges.querySelector('[data-countdown-sale-badge]')) {
            cardBadges.querySelector('[data-countdown-sale-badge]').remove();
          }
        }
      }

      isProductOnSale(product) {
        let comparePrice = product && product.compare_at_price ? product.compare_at_price : null;
        let price = product && product.price ? product.price : null;

        if (comparePrice && price && comparePrice > price) {
          return true;
        }

        return false;
      }

      updateSalesBadge(cardBadges, product) {
        if (this.isProductOnSale(product)) {
          // Add the badge
          if (!cardBadges.querySelector('[data-sale-badge]')) {
            // Update the badge text or attributes
            const onSaleBadgeText = this.translations.on_sale;

            // Create the badge
            const onSaleBadge = document.createElement('span');
            // onSaleBadge.id = `NoMediaStandardBadge-${this.sectionId}-${product.id}`;
            onSaleBadge.classList.add('badge', `badge--bottom-left`, `color-${this.settings.sale_badge_color_scheme}`, `badge--promo-${this.settings.promotion_badge_design_scheme}`);
            onSaleBadge.dataset.saleBadge = '';
            onSaleBadge.textContent = onSaleBadgeText;

            cardBadges.appendChild(onSaleBadge);
          } else {
            // Remove the badge
            if (cardBadges.querySelector('[data-sale-badge]')) {
              cardBadges.querySelector('[data-sale-badge]').remove();
            }
          }
        }
      }

      updateProductImagesCarousel(product, isVariant) {
        if (isVariant) {
          this.swapProductVariantImages(product);
        } else {
          this.swapProductImages(product);
        }
      }

      swapProductVariantImages(product) {
        const newImages = []; // Array of new images
        const productUrl = this.dataset.productUrl;
        let productImageWidth = 332;
        // If product grid view showing 1 product per column
        if (this.gridView == 1) {
          productImageWidth = 664;
        }

        const featuredImage = product.featured_image || product.media?.[0];
        if (!featuredImage?.src) return;
        const featuredImageAlt = featuredImage.alt || product.title;
        newImages.push({ src: featuredImage.src, srcset: `${featuredImage.src} 2x`, alt: featuredImageAlt });

        if (this.showLifeStyleImageFirst && (newImages.length > 1)) {
          // Swap the first and second images
          const temp = newImages[0];
          newImages[0] = newImages[1];
          newImages[1] = temp;
        }

        this.replaceCarouselSlides(newImages, productUrl, productImageWidth);
      }

      swapProductImages(product) {
        const newImages = []; // Array of new images
        const productUrl = `/products/${product.handle}`;
        let productImageWidth = 332;
        // If product grid view showing 1 product per column
        if (this.gridView == 1) {
          productImageWidth = 664;
        }

        // product.media.forEach(media => {
        //   const mediaAlt = media.alt || product.title;
        //   newImages.push({ src: media.src, srcset: `${media.src} 2x`, alt: mediaAlt });
        // });
        // Reducing the amount of times we loop through the media to improve performance
        for (const [index, media] of (product.media || []).entries()) {
          const mediaAlt = media.alt || product.title;
          newImages.push({ src: media.src, srcset: `${media.src} 2x`, alt: mediaAlt });
          if (index > 1) break; // exits completely
        }


        if (this.showLifeStyleImageFirst && (newImages.length > 1)) {
          // Swap the first and second images
          const temp = newImages[0];
          newImages[0] = newImages[1];
          newImages[1] = temp;
        }

        this.replaceCarouselSlides(newImages, productUrl, productImageWidth);
      }

      replaceCarouselSlides(images, productUrl, productImageWidth) {
        const swiperWrapper = this.querySelector('.swiper-wrapper');
        if (!swiperWrapper || !images.length) return;

        swiperWrapper.innerHTML = '';

        images.slice(0, 2).forEach((image, index) => {
          const slide = this.createProductImageSlide(image, productUrl, productImageWidth, index);

          if (this.swiper || index === 0) {
            swiperWrapper.appendChild(slide);
            return;
          }

          const template = document.createElement('template');
          template.dataset.carouselSlideTemplate = '';
          template.content.appendChild(slide);
          swiperWrapper.appendChild(template);
        });

        if (this.swiper) {
          this.swiper.update();
          this.swiper.slideTo(0, 0);
        }
      }

      createProductImageSlide(image, productUrl, productImageWidth, index) {
        const slide = document.createElement('a');
        slide.classList.add('swiper-slide');
        slide.href = productUrl;

        const picture = document.createElement('picture');
        picture.classList.add('product-card__picture');

        const img = document.createElement('img');
        img.src = this.addOrUpdateUrlParam(image.src, 'width', productImageWidth);
        img.alt = image.alt || '';
        img.width = productImageWidth;
        img.height = productImageWidth;
        img.loading = 'lazy';

        if (this.gridView == 1) {
          img.srcset = [200, 300, 400, 445, 600, 800, 900]
            .map((width) => `${this.addOrUpdateUrlParam(image.src, 'width', width)} ${width}w`)
            .join(', ');
          img.sizes = '(max-width: 989px) 100vw, 23vw';
        } else {
          img.srcset = [100, 200, 300]
            .map((width) => `${this.addOrUpdateUrlParam(image.src, 'width', width)} ${width}w`)
            .join(', ');
          img.sizes = '(max-width: 989px) 45vw, 23vw';
        }

        const badge = document.createElement('div');
        badge.classList.add('product-card-feature-badge');
        badge.dataset.position = String(index + 1);

        picture.append(img, badge);
        slide.appendChild(picture);

        return slide;
      }

      hydrateCarouselSlides() {
        const swiperWrapper = this.querySelector('.swiper-wrapper');
        if (!swiperWrapper) return;

        swiperWrapper.querySelectorAll('template[data-carousel-slide-template]').forEach((template) => {
          swiperWrapper.appendChild(template.content.cloneNode(true));
          template.remove();
        });
      }

      initCarousel() {
        const swiperContainer = this.querySelector('.swiper-container');
        if (!swiperContainer) return; // No carousel to initialize

        const swiperWrapper = this.querySelector('.swiper-wrapper');
        const bannerSpeed = swiperWrapper.dataset.transition;
        this.hydrateCarouselSlides();

        // Check the number of slides
        const slides = swiperWrapper.querySelectorAll('.swiper-slide');
        const hasMultipleSlides = slides.length > 1;

        if (!hasMultipleSlides) {
          const nextButton = swiperContainer.querySelector('.swiper-button-next');
          const prevButton = swiperContainer.querySelector('.swiper-button-prev');
          if (nextButton) nextButton.style.display = 'none';
          if (prevButton) prevButton.style.display = 'none';
          return null;
        }

        // Initialize Swiper
        this.swiper = new Swiper(swiperContainer, {
          // Enable Lazy Loading
          lazy: {
            loadPrevNext: true, // Optional: Preload images before and after the current slide
          },
          preloadImages: false, // Prevent Swiper from preloading all images
          slidesPerView: 1,
          spaceBetween: 0,
          speed: bannerSpeed,
          loop: false,
          navigation: hasMultipleSlides ? {
            nextEl: swiperContainer.querySelector('.swiper-button-next'),
            prevEl: swiperContainer.querySelector('.swiper-button-prev'),
          } : false,
          pagination: {
            el: swiperContainer.querySelector('.swiper-pagination'),
            clickable: true,
          }
        });

        return this.swiper;
      }

      addOrUpdateUrlParam(url, key, value) {
        const [baseUrl, queryString] = url.split('?');
        const params = new URLSearchParams(queryString || '');

        params.set(key, value); // Add or update the width param

        return `${baseUrl}?${params.toString()}`;
      }

      updateProductHeading(product) {
        const model = product.metafields.model || '';
        if (this.productHeadings && this.productHeadings.length > 0) {
          this.productHeadings.forEach(productHeading => {
            productHeading.textContent = product.title.replace(model, '');
            const productUrl = `/products/${product.handle}`;
            productHeading.href = productUrl;
          });
        }
      }

      updateProductPricing(product) {
        const priceItem = this.querySelectorAll('.price-item');
        const couponDiscount = this.activeCouponPromo ? this.activeCouponPromo.is_percent ? (product.price * +this.activeCouponPromo.amount_off) / 100 : +this.activeCouponPromo.amount_off * 100 : 0;

        let price = (product.price - couponDiscount) / 100; // Convert cents to dollars
        let regularPrice = product.compare_at_price ? product.compare_at_price / 100 : this.activeCouponPromo ? (product.price / 100) : null;
        let onSale = false;

        if (regularPrice) {
          onSale = true;
        }

        if (onSale) {
          // Show the sale price and strike through the regular price
          this.prices.forEach(element => {
            element.classList.add('price--on-sale');
          });
          this.salePrices.forEach(element => {
            this.formatPrice(element.querySelector('.price__sale .price-item.price-item--regular'), regularPrice);
            this.formatPrice(element.querySelector('.price__sale .price-item.price-item--sale'), price);
            element.style.display = 'flex';
          });
          this.regularPrices.forEach(element => {
            element.style.display = 'none';
            element.querySelector('.price__regular .price-item.price-item--regular').textContent = '';
          });
        } else {
          // Only show the regular price
          this.prices.forEach(element => {
            element.classList.remove('price--on-sale');
          });
          this.salePrices.forEach(element => {
            element.style.display = 'none';
            element.querySelector('.price__sale .price-item.price-item--regular').textContent = '';
            element.querySelector('.price__sale .price-item.price-item--sale').textContent = '';
          });
          this.regularPrices.forEach(element => {
            this.formatPrice(element.querySelector('.price__regular .price-item.price-item--regular'), price);
            element.style.display = 'block';
          });
        }

        if (priceItem) {
          priceItem.textContent = `$${product.price.toFixed(2)}`;
        }
      }

      /**
       *
       * @param {HTMLElement} priceWrapperEl
       * @param {number} price
       */
      formatPrice(priceWrapperEl, price) {
        const showCurrency = this.settings.currency_code_enabled;
        const currency = Shopify.currency.active;
        const centsPrice = (price.toFixed(2).split('.')[1] ?? '00') || '00';
        price = Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace('.00', '');
        if (centsPrice === '00')
          priceWrapperEl.textContent = `$${price}${showCurrency ? (' ' + currency) : ''}`;
        else {
          const priceDollarsEl = priceWrapperEl.querySelector('.price-item-dollars');
          const priceCentsEl = priceWrapperEl.querySelector('.price-item-cents');
          const priceDollars = `$${price.split('.')[0]}${showCurrency ? (' ' + currency) : ''}`
          if (priceDollarsEl && priceCentsEl) {
            priceDollarsEl.textContent = priceDollars;
            priceCentsEl.textContent = centsPrice;
          }
          else {
            priceWrapperEl.innerHTML = `
              <span class="price-item-dollars">${priceDollars}</span><span class="price-item-dot">.</span><span class="price-item-cents">${centsPrice}</span>
            `
          }

        }
      }
    }
  );
}

(function initProductCardBackNavigationCartSync() {
  if (window.productCardBackNavigationCartSyncInitialized) return;
  window.productCardBackNavigationCartSyncInitialized = true;

  const syncProductCardsWithCart = () => {
    const productCards = document.querySelectorAll('product-card');
    if (!productCards.length) return;

    fetch('/cart.js')
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
        return response.json();
      })
      .then((cartData) => {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartDataLoaded, cartData || {});
          return;
        }

        productCards.forEach((productCard) => {
          productCard.updateProductQuantitiesFromCart?.(cartData || {});
        });
      })
      .catch((error) => {
        console.warn('Unable to sync product card cart quantities:', error);
      });
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncProductCardsWithCart();
  });
})();
